import { types, flow } from 'mobx-state-tree';
import { ApiStore } from './ApiStore';

const LeaderboardEntry = types.model('LeaderboardEntry', {
    userId: types.string,
    displayName: types.string,
    points: types.number,
    rank: types.number,
    isCurrentUser: types.optional(types.boolean, false),
});

const LeaderboardStoreGroup = types.model('LeaderboardStoreGroup', {
    id: types.string,
    name: types.string,
    memberIds: types.optional(types.array(types.string), []),
});

const LeaderboardSeason = types.model('LeaderboardSeason', {
    periodStart: types.string,
    periodEnd: types.maybeNull(types.string),
    label: types.maybeNull(types.string),
    cadence: types.maybeNull(types.string),
    scoringMode: types.maybeNull(types.string),
    entries: types.optional(types.array(LeaderboardEntry), []),
    groups: types.optional(types.array(LeaderboardStoreGroup), []),
});

const _leaderboardStore = types.model({
    enabled: types.optional(types.boolean, false),
    cadence: types.maybeNull(types.string),
    periodStart: types.maybeNull(types.string),
    currentUserEligibleToOptIn: types.optional(types.boolean, false),
    currentUserOptIn: types.optional(types.boolean, false),
    entries: types.optional(types.array(LeaderboardEntry), []),
    groups: types.optional(types.array(LeaderboardStoreGroup), []),
    currentUserGroupId: types.maybeNull(types.string),
    canBrowseAllGroups: types.optional(types.boolean, false),
    seasons: types.optional(types.array(LeaderboardSeason), []),
    seasonsLoaded: types.optional(types.boolean, false),
    loaded: types.optional(types.boolean, false),
});

export const LeaderboardStore = types
    .compose('LeaderboardStore', _leaderboardStore, ApiStore)
    .actions(self => ({
        // Lazily loaded by the dashboard widget (NOT in CompanyInitializer) so disabled
        // companies and non-dashboard pages pay nothing. Returns a points-only payload.
        load: flow(function* load(companyId) {
            const data = yield self.getApi(`/companies/${companyId}/leaderboard`);
            if (!data) return;
            self.enabled = !!data.enabled;
            self.cadence = data.cadence ?? null;
            self.periodStart = data.periodStart ?? null;
            self.currentUserEligibleToOptIn = !!data.currentUserEligibleToOptIn;
            self.currentUserOptIn = !!data.currentUserOptIn;
            self.entries = (Array.isArray(data.entries) ? data.entries : []).map(e => ({
                userId: e.userId,
                displayName: e.displayName,
                points: e.points,
                rank: e.rank,
                isCurrentUser: !!e.isCurrentUser,
            }));
            self.groups = (Array.isArray(data.groups) ? data.groups : []).map(g => ({
                id: g.id,
                name: g.name,
                memberIds: Array.isArray(g.memberIds) ? g.memberIds : [],
            }));
            self.currentUserGroupId = data.currentUserGroupId ?? null;
            self.canBrowseAllGroups = !!data.canBrowseAllGroups;
            self.loaded = true;
        }),
        // Admin-only: frozen past-season standings. Returns names + points only.
        loadSeasons: flow(function* loadSeasons(companyId) {
            const data = yield self.getApi(`/companies/${companyId}/leaderboard/seasons`);
            if (!data) return;
            self.seasons = (Array.isArray(data.seasons) ? data.seasons : []).map(s => ({
                periodStart: s.periodStart,
                periodEnd: s.periodEnd ?? null,
                label: s.label ?? null,
                cadence: s.cadence ?? null,
                scoringMode: s.scoringMode ?? null,
                entries: (Array.isArray(s.entries) ? s.entries : []).map(e => ({
                    userId: e.userId, displayName: e.displayName, points: e.points, rank: e.rank, isCurrentUser: false,
                })),
                groups: (Array.isArray(s.groups) ? s.groups : []).map(g => ({
                    id: g.id, name: g.name, memberIds: Array.isArray(g.memberIds) ? g.memberIds : [],
                })),
            }));
            self.seasonsLoaded = true;
        }),
    }));
